/**
 * Domain enums — defined once, consumed by the API (Prisma + validation), the
 * web app (chip labels, filter options) and the tests.
 *
 * Every value below comes from the backlog's stated states or the mocked FE's
 * existing value sets, so a label rendered in the UI and a value stored in the
 * database can never drift apart. The only additions are the `UNKNOWN` members,
 * which exist for the reason S04 gives: safe defaults when the analyzer returns
 * something we do not recognise.
 */
import { z } from 'zod';

/** Helper: build a Zod enum plus its readonly value tuple from one declaration. */
function domainEnum<const T extends readonly [string, ...string[]]>(values: T) {
  return { values, schema: z.enum(values) } as const;
}

// ---------------------------------------------------------------------------
// Calls (S07)
// ---------------------------------------------------------------------------

/**
 * An inbound call's status: IN_PROGRESS -> COMPLETED | ABANDONED | FAILED.
 *
 * Created by the platform's lifecycle events, never by us (DV2). COMPLETED means
 * the caller spoke at least once; ABANDONED that they never did — hung up during
 * the greeting or stayed silent. FAILED is ours: no end event arrived, or the
 * transcript could not be read. A call never moves back to IN_PROGRESS.
 */
export const CallStatus = domainEnum(['IN_PROGRESS', 'COMPLETED', 'ABANDONED', 'FAILED'] as const);
export type CallStatus = z.infer<typeof CallStatus.schema>;

/** Statuses a call ends in. */
export const FINISHED_CALL_STATUSES = [
  'COMPLETED',
  'ABANDONED',
  'FAILED',
] as const satisfies readonly CallStatus[];

/** Where a call event came from, for the audit trail S07 requires. */
export const CallEventSource = domainEnum(['WEBHOOK', 'POLL', 'WORKER'] as const);
export type CallEventSource = z.infer<typeof CallEventSource.schema>;

/** Who spoke a transcript turn. Sirma's `user` role is the caller. */
export const TranscriptRole = domainEnum(['AGENT', 'CALLER'] as const);
export type TranscriptRole = z.infer<typeof TranscriptRole.schema>;

export const TranscriptSource = domainEnum(['SIRMA_SESSION', 'WEBHOOK'] as const);
export type TranscriptSource = z.infer<typeof TranscriptSource.schema>;

// ---------------------------------------------------------------------------
// Analysis results (S04, S08)
// ---------------------------------------------------------------------------

/**
 * Why the caller rang (Q5: the Sirma AI demo line's vocabulary).
 *
 * `UNKNOWN` is never offered as a filter and never guessed at: it is what an
 * unrecognised analyzer value becomes, recorded with a mapping warning.
 */
export const CallReason = domainEnum([
  'INFORMATION',
  'BOOKING',
  'CHANGE_OR_CANCEL',
  'COMPLAINT',
  'SUPPORT',
  'OTHER',
  'UNKNOWN',
] as const);
export type CallReason = z.infer<typeof CallReason.schema>;

/** The reasons the filter offers — `UNKNOWN` deliberately excluded. */
export const CALL_REASON_FILTERS = [
  'INFORMATION',
  'BOOKING',
  'CHANGE_OR_CANCEL',
  'COMPLAINT',
  'SUPPORT',
  'OTHER',
] as const satisfies readonly CallReason[];

/**
 * How the call ended for the caller.
 *
 * ESCALATION_REQUESTED is read generously (DV4): a missed escalation leaves a
 * caller waiting for nobody, a false one costs a colleague a minute.
 *
 * TRANSFERRED is not the analyzer's opinion: it is set when the platform's own
 * record of the call shows the agent forwarded it to a colleague. Nobody is
 * left waiting, so it is not a follow-up.
 */
export const CallResolution = domainEnum([
  'RESOLVED',
  'UNRESOLVED',
  'ESCALATION_REQUESTED',
  'CALLBACK_REQUESTED',
  'TRANSFERRED',
  'UNKNOWN',
] as const);
export type CallResolution = z.infer<typeof CallResolution.schema>;

/** The resolutions the filter offers — `UNKNOWN` deliberately excluded. */
export const CALL_RESOLUTION_FILTERS = [
  'RESOLVED',
  'UNRESOLVED',
  'ESCALATION_REQUESTED',
  'CALLBACK_REQUESTED',
  'TRANSFERRED',
] as const satisfies readonly CallResolution[];

/** Resolutions that need a person to follow up. */
export const FOLLOW_UP_RESOLUTIONS = [
  'ESCALATION_REQUESTED',
  'CALLBACK_REQUESTED',
] as const satisfies readonly CallResolution[];

// ---------------------------------------------------------------------------
// Export (S08)
// ---------------------------------------------------------------------------

export const ExportFormat = domainEnum(['CSV', 'XLSX'] as const);
export type ExportFormat = z.infer<typeof ExportFormat.schema>;

export const ExportStatus = domainEnum([
  'QUEUED',
  'GENERATING',
  'READY',
  'FAILED',
  'EXPIRED',
] as const);
export type ExportStatus = z.infer<typeof ExportStatus.schema>;

// ---------------------------------------------------------------------------
// Agents (S10)
// ---------------------------------------------------------------------------

export const AgentAuditAction = domainEnum([
  'SYNCED',
  'UPDATED',
  'PUSH_FAILED',
  'CONFLICT',
] as const);
export type AgentAuditAction = z.infer<typeof AgentAuditAction.schema>;

// ---------------------------------------------------------------------------
// Notifications (S12)
// ---------------------------------------------------------------------------

export const NotificationKind = domainEnum(['USAGE', 'CALL', 'SYSTEM'] as const);
export type NotificationKind = z.infer<typeof NotificationKind.schema>;

/** Severity. Maps to the mocked FE's four levels: info / ok / warn / bad. */
export const NotificationSeverity = domainEnum(['INFO', 'OK', 'WARNING', 'CRITICAL'] as const);
export type NotificationSeverity = z.infer<typeof NotificationSeverity.schema>;

export const NotificationLinkType = domainEnum(['INTERACTION', 'NONE'] as const);
export type NotificationLinkType = z.infer<typeof NotificationLinkType.schema>;

// ---------------------------------------------------------------------------
// Webhook ingest (S04)
// ---------------------------------------------------------------------------

/**
 * The five event types Sirma's `/client/` webhook surface publishes, confirmed
 * against `backend`'s `WebhookEventType.kt`.
 */
export const SirmaWebhookEventType = domainEnum([
  'agent.execution.started',
  'agent.execution.completed',
  'agent.execution.failed',
  'audioagent.session.started',
  'audioagent.session.ended',
] as const);
export type SirmaWebhookEventType = z.infer<typeof SirmaWebhookEventType.schema>;

export const WebhookDeliveryStatus = domainEnum([
  'RECEIVED',
  'PROCESSED',
  'REJECTED',
  'DUPLICATE',
] as const);
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatus.schema>;
